import { useNavigate, Link } from "react-router-dom";
import { LifeBuoy, LogIn, KeyRound, Receipt, Download, Trash2, Mail } from "lucide-react";

// Public, unauthenticated Molaroo Support page. Doubles as the App Store Connect Support URL.
// Contains no purchasing CTAs, no external subscription instructions and no internal technical
// or Stripe implementation detail.

function Item({ icon: Icon, title, children, testId }) {
  return (
    <div className="df-card p-4" data-testid={testId}>
      <div className="flex items-center gap-3 mb-1.5">
        <span className="h-10 w-10 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center shrink-0">
          <Icon size={18} />
        </span>
        <div className="font-medium text-slate-900">{title}</div>
      </div>
      <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

export default function Support() {
  const navigate = useNavigate();

  return (
    <div className="df-shell min-h-screen df-scroll">
      <div className="max-w-2xl mx-auto px-6 py-8 df-fade-in">
        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/login"))}
          data-testid="support-back" className="text-sm text-[#0D786A] font-medium mb-4 df-pressable">← Back</button>

        <div className="flex items-center gap-3 mb-6">
          <img src="/molaroo-mark.png" alt="Molaroo" className="h-12 w-12 rounded-2xl object-contain bg-white shadow-sm" />
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900 leading-none flex items-center gap-2">
              <LifeBuoy size={20} className="text-[#0D786A]" /> Molaroo Support
            </h1>
            <div className="text-xs text-slate-400 mt-1">Molaroo by NavScora</div>
          </div>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed mb-5">
          Need help with Molaroo? We're here to help with account access, using Molaroo, data and
          privacy questions, and general support.
        </p>

        <div className="space-y-3">
          <Item icon={Mail} title="Contact us" testId="support-contact">
            The quickest way to reach us is by email at{" "}
            <a href="mailto:support@navscora.com" data-testid="support-email-text"
              className="text-[#0D786A] font-medium">support@navscora.com</a>. We aim to respond
            within a reasonable time.
            <div className="mt-3">
              <a href="mailto:support@navscora.com" data-testid="support-email"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#0D786A] text-white text-sm font-semibold df-pressable">
                <Mail size={16} /> Contact Support
              </a>
            </div>
          </Item>

          <Item icon={LogIn} title="Account & sign-in help" testId="support-login">
            Sign in at the Molaroo login screen with the email and password you used to create your
            account. If you have two-factor authentication enabled, you'll also enter a 6-digit code
            (or a backup code). If you can't sign in, first confirm you're using the correct email
            address, then reset your password below.
          </Item>

          <Item icon={KeyRound} title="Reset your password" testId="support-password">
            Use{" "}
            <Link to="/forgot-password" data-testid="support-forgot-link" className="text-[#0D786A] font-medium">Forgot password?</Link>{" "}
            on the sign-in screen. We'll email a secure reset link to your account email address.
            Open the link and choose a new password (at least 8 characters). If the email doesn't
            arrive, check your spam folder and confirm the address is correct.
          </Item>

          <Item icon={Receipt} title="Receipts & documents" testId="support-receipts">
            When adding or editing an expense you can attach a receipt or document. Choose
            <strong> Scan / Photo</strong> to capture with your camera, or <strong>Choose file</strong>
            {" "}to pick an existing image or PDF. Scanned photos are saved as a PDF. You can view,
            replace or remove an attachment at any time.
          </Item>

          <Item icon={Download} title="Export your data" testId="support-export">
            Go to <strong>More → Privacy &amp; Data → Export my data</strong> to download all your
            Molaroo records as a JSON file. This is separate from the Accountant Pack and excludes
            passwords, MFA secrets and security tokens.
          </Item>

          <Item icon={Trash2} title="Delete your account" testId="support-delete">
            Go to <strong>More → Privacy &amp; Data → Delete account</strong>. You'll confirm by
            typing <strong>DELETE MY ACCOUNT</strong> and entering your password. This permanently
            removes your profile, practices, work, payments, expenses, reserves and uploaded files.
            Export any records you wish to keep first. This is different from "Reset my data".
          </Item>
        </div>

        <div className="df-card p-4 mt-3" data-testid="support-legal">
          <div className="text-sm font-semibold text-slate-900 mb-1">Policies</div>
          <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
            <Link to="/privacy" data-testid="support-privacy-link" className="text-[#0D786A] font-medium">Privacy Policy</Link>
            <span aria-hidden>·</span>
            <Link to="/terms" data-testid="support-terms-link" className="text-[#0D786A] font-medium">Terms &amp; Conditions</Link>
          </div>
        </div>

        <div className="df-card p-4 mt-3" data-testid="support-provider">
          <div className="text-sm font-semibold text-slate-900 mb-1">Provider</div>
          <div className="text-sm text-slate-600 leading-relaxed not-italic">
            Molaroo is operated by NavScora Pty Ltd<br />
            ACN 73 702 089 021<br />
            PO Box 5387, Stafford Heights QLD 4053, Australia
          </div>
        </div>

        <footer className="mt-8 pt-6 border-t border-slate-200 text-center space-y-1" data-testid="support-footer">
          <div className="text-sm text-slate-500">Molaroo is a product of NavScora Pty Ltd.</div>
          <div className="text-xs text-slate-400">© 2026 NavScora Pty Ltd</div>
        </footer>
      </div>
    </div>
  );
}
